import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Building2 } from "lucide-react";

// Import các sub-components (Giả sử bạn tách file, hoặc để cùng file phía dưới)
import CustomerHeader from './components/CustomerHeader';
import SummaryStats from './components/SummaryStats';
import CustomerFilters from './components/CustomerFilters';
import CustomerTable from './components/CustomerTable';
import Pagination from './components/Pagination';
import { customerService } from '../../../services/apiService';
import Customer360Section from './components/Customer360Section';
import { setMerchantId } from "../../../features/global/globalSlice";

const Customer = () => {
  // --- 1. LOGIC & STATE ---
  const dispatch = useDispatch();
  const merchantId = useSelector((state) => state.global.merchant_id);

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [pagination, setPagination] = useState({ limit: 50, offset: 0 });

  // State Date Range cho Customer360
  const now = new Date();
  const firstDay = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1)).toISOString().split("T")[0];
  const lastDay = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0)).toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);

  // State điều phối luồng gọi tuần tự Customer360
  const [trigger360, setTrigger360] = useState(false);

  // --- 2. HANDLERS ---
  const handleSwitchMerchant = (newMerchantId) => {
    dispatch(setMerchantId(newMerchantId));
    setSearchKeyword("");
    setPagination({ limit: 50, offset: 0 });
  };

  const handleSearchCustomer = (keyword) => {
    setSearchKeyword(keyword);
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  const handlePageChange = (direction) => {
    setPagination((prev) => ({
      ...prev,
      offset:
        direction === "next"
          ? prev.offset + prev.limit
          : Math.max(0, prev.offset - prev.limit),
    }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination({
      limit: newLimit,
      offset: 0, // Reset về trang đầu tiên (offset = 0) mỗi khi đổi limit
    });
  };

  useEffect(() => {
    // Reset trigger mỗi khi các param thay đổi để đảm bảo Totals chạy đầu tiên
    setTrigger360(false);

    const fetchCustomers = async () => {
      // 1. Kiểm tra điều kiện đầu vào
      if (!merchantId) return;

      setLoading(true);
      try {
        // 2. Gọi API qua Service (API Totals - gọi đầu tiên)
        const response = await customerService.getTotals(
          merchantId,
          pagination.limit,
          pagination.offset,
          searchKeyword,
        );

        // 3. Axios trả về data nằm trong response.data
        setCustomers(response.data || []);
      } catch (error) {
        console.error("Error fetching customers:", error);
        setCustomers([]);
      } finally {
        setLoading(false);
        // Gọi xong API thứ nhất -> bật công tắc cho phép 9 API tiếp theo chạy
        setTrigger360(true);
      }
    };

    fetchCustomers();
  }, [merchantId, searchKeyword, pagination.offset, pagination.limit]);

  // --- 4. SUMMARY CALCULATION (Gọi API riêng biệt để lấy tổng quan, không phụ thuộc bộ lọc danh sách bên dưới) ---
  const [summary, setSummary] = useState({
    totalCustomers: 0,
    totalWithPhone: 0,
    totalMB: 0,
    totalOther: 0,
    totalWithMBPhone: 0,
    totalWithOtherPhone: 0
  });

  useEffect(() => {
    if (!merchantId) return;

    const fetchSummary = async () => {
      try {
        const res = await customerService.getInsightCustomerStats(merchantId);
        if (res?.data) {
          setSummary({
            totalCustomers: res.data.total_customers || 0,
            totalWithPhone: res.data.total_phones || 0,
            totalMB: res.data.total_mb_bank || 0,
            totalOther: res.data.total_non_mb || 0,
            totalWithMBPhone: res.data.total_mb_phones || 0,
            totalWithOtherPhone: res.data.total_non_mb_phones || 0,
          });
        }
      } catch (err) {
        console.error("Error fetching customer stats:", err);
      }
    };

    fetchSummary();
  }, [merchantId]);

  // --- 5. RENDER ---
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800">
      <CustomerHeader
        merchantId={merchantId}
        onSwitchMerchant={handleSwitchMerchant}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!merchantId ? (
          <EmptyState />
        ) : (
          <>
            <SummaryStats summary={summary} />

            {/* Bộ lọc Thời gian cho Customer360 */}
            <div className="mt-8 mb-4 flex flex-col sm:flex-row sm:items-center justify-between bg-white p-5 rounded-xl shadow-sm border border-slate-200 gap-4">
              <h2 className="text-xl font-bold text-slate-800">Phân tích Chuyên sâu (Customer 360)</h2>
              <div className="flex items-center space-x-4">
                <div className="flex flex-col">
                  <label className="text-xs text-slate-500 mb-1.5 font-semibold uppercase tracking-wider">Từ ngày</label>
                  <input 
                    type="date" 
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all w-36"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-slate-500 mb-1.5 font-semibold uppercase tracking-wider">Đến ngày</label>
                  <input 
                    type="date" 
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:bg-white transition-all w-36"
                  />
                </div>
              </div>
            </div>

            {/* Gọi Component Customer360 */}
            <Customer360Section 
              merchant_id={merchantId} 
              start_date={startDate} 
              end_date={endDate}
              trigger360={trigger360}
            />

            <div className="mt-8"></div>

            <CustomerFilters
              loading={loading}
              onSearch={handleSearchCustomer}
            />

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <CustomerTable customers={customers} loading={loading} />

              <Pagination
                pagination={pagination}
                loading={loading}
                currentCount={customers.length}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

// Component nhỏ hiển thị khi chưa chọn Merchant
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 max-w-md">
      <div className="bg-slate-100 p-4 rounded-full inline-block mb-4">
        <Building2 size={40} className="text-slate-400" />
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">
        Chưa chọn Cửa hàng
      </h3>
      <p className="text-slate-500 mb-6">
        Sử dụng thanh tìm kiếm phía trên để nhập Merchant ID.
      </p>
    </div>
  </div>
);

export default Customer;
